import { useState } from 'react'
import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { IConektaCard, IConektaTokenResponse } from 'models/payments'
import { successToastOptions } from 'lib/utils/toast.utils'
import useDialog from 'hooks/Shared/useDialog'
import { IApiError } from 'services/Shared/shared.models'
import { createPaymentOrder } from 'services/Payments/payments.services'
import { IUsePaymentsContainer } from 'containers/Account/Payments/PaymentsContainer/usePaymentsContainer'

interface IUseConektaTokenizeCardProps
  extends Pick<
    IUsePaymentsContainer,
    | 'refetchBillingInformation'
    | 'refetchPaymentsHistory'
    | 'goBackToPaymentsOverview'
  > {
  selectedWorkspaceId: number
  idPlan: number
  periodEnd: string
  periodStart: string
  recurrent: boolean
}

const useConektaTokenizeCard = ({
  refetchBillingInformation,
  refetchPaymentsHistory,
  goBackToPaymentsOverview,
  selectedWorkspaceId,
  idPlan,
  periodEnd,
  periodStart,
  recurrent,
}: IUseConektaTokenizeCardProps): UseMutationResult<
  void,
  IApiError,
  IConektaCard
> => {
  const dialog = useDialog()

  const [attempts, setAttempts] = useState(0)

  const cleanup = () => {
    refetchBillingInformation()
    refetchPaymentsHistory()
    goBackToPaymentsOverview()
  }

  const mutation = useMutation<void, IApiError, IConektaCard>(
    async requestBody => {
      const tokenizedCardHeaders = new Headers()
      tokenizedCardHeaders.append('Accept-Language', 'es')
      tokenizedCardHeaders.append('content-type', 'application/json')
      tokenizedCardHeaders.append(
        'accept',
        'application/vnd.conekta-v2.1.0+json'
      )
      tokenizedCardHeaders.append(
        'Conekta-Client-User-Agent',
        '{"agent":"Conekta JavascriptBindings-AJAX/v2.0.0 build 2.0.17"}'
      )
      tokenizedCardHeaders.append(
        'authorization',
        `Basic ${window.btoa(process.env.REACT_APP_CONEKTA_API_KEY!)}`
      )

      const tokenizedCardResponse = await fetch(
        'https://api.conekta.io/tokens',
        {
          method: 'POST',
          body: JSON.stringify({
            card: requestBody,
          }),
          headers: tokenizedCardHeaders,
        }
      ).then(async response => {
        if (!response.ok) {
          const resolvedResponse = await response.json()
          if (
            resolvedResponse.details &&
            resolvedResponse.details[0]?.message
          ) {
            throw new Error(resolvedResponse.details[0].message)
          }
          throw new Error('Error al tokenizar los datos de la tarjeta')
        }

        return response
      })

      const tokenizedCardData: IConektaTokenResponse =
        await tokenizedCardResponse.json()

      const makePayment = {
        idPlanToPay: idPlan,
        recurrent,
        periodStart,
        periodEnd,
        tokenCard: tokenizedCardData.id,
      }

      return await createPaymentOrder(selectedWorkspaceId, makePayment)
    },
    {
      retry: 0,
      onSuccess: () => {
        const message = recurrent ? 'Domiciliación exitosa : ' : 'Pago exitoso'
        cleanup()
        toast.success(message, successToastOptions)
      },

      onError: ({ message }, variables) => {
        setAttempts(prevState => prevState + 1)

        const errorTitle = recurrent
          ? 'No fue posible realizar la domiciliación'
          : 'No se pudo realizar el pago'
        const errorMessage = recurrent ? 'Por favor intenta de nuevo' : message

        dialog.danger({
          title: errorTitle,
          subtitle: errorMessage,
          message: attempts
            ? 'Por favor intenta de nuevo más tarde'
            : undefined,
          primaryButtonText: !attempts ? 'Reintentar' : undefined,
          secondaryButtonText: !attempts ? 'Cancelar' : undefined,
          onPrimaryClick: attempts
            ? cleanup
            : () => {
                setTimeout(() => {
                  mutation.mutate(variables)
                }, 300)
              },
          onSecondaryClick: cleanup,
        })
      },
    }
  )

  return mutation
}

export default useConektaTokenizeCard
